import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Bell, X } from 'lucide-react'
import { Button } from '@/components/ui/Button.tsx'
import { useToast } from '@/lib/hooks/useToast'
import showSuccessToast from '@/components/common/showToastSuccess.tsx'
import { useUserStore } from '@/store/user'
import { usePushPromptStore } from '@/store/usePushPromptStore'
import { registerPush } from '@/lib/push/registerPush'
import { useIsBottomSurfaceClaimedAbove } from '@/lib/hooks/bottomSurface'
import { useIsAnyDialogOpen } from '@/lib/hooks/useIsAnyDialogOpen'

/**
 * Post-login soft prompt: asks IN-APP first, so the OS permission dialog only
 * ever follows a deliberate tap. Non-modal and dismissible by design — a
 * refused OS prompt is unrecoverable from inside the app (the web blocks the
 * origin; iOS sends the user to system Settings), so it must never be triggered
 * by merely opening the app.
 *
 * Visibility is owned by usePushPromptGate; this component only renders and
 * handles the three outcomes.
 */
export default function PushPromptCard() {
  const { t } = useTranslation()
  const { toast } = useToast()
  const userId = useUserStore(s => s.user?.id ?? null)
  const isVisible = usePushPromptStore(s => s.isVisible)
  const snooze = usePushPromptStore(s => s.snooze)
  const dismissForever = usePushPromptStore(s => s.dismissForever)
  const clearSuppression = usePushPromptStore(s => s.clearSuppression)
  const suppressForSession = usePushPromptStore(s => s.suppressForSession)
  const [busy, setBusy] = useState(false)
  // Yields to the native update surface. Measured 2026-08-26 at 375px: this card
  // is anchored 84px above the tab bar at z-95 and the update card 12px above it
  // at z-120, both `inset-x-4`; at their real heights the update card covered
  // this one's whole Enable/Later/Never row, so the offer was readable and
  // unpressable. See `bottomSurface.ts` for the ladder.
  const isOutranked = useIsBottomSurfaceClaimedAbove('push-prompt')
  // The gate only stops the card from being REVEALED over a dialog; this covers
  // a dialog opened while the card is already up. The card lives outside every
  // dialog portal, so under a modal it is drawn but unpressable (Radix's body
  // pointer-events guard). Render-only on purpose: `isVisible` stays set, so the
  // same card is back the moment the dialog closes. See RETRACTING_REASONS in
  // usePushPromptGate.
  const isDialogOpen = useIsAnyDialogOpen()

  if (!isVisible || userId === null || isOutranked || isDialogOpen) return null

  const onEnable = async () => {
    setBusy(true)
    try {
      const result = await registerPush(userId)
      if (result.status === 'registered') {
        // Clear any earlier suppression so a future Disable → re-enable cycle
        // starts from a clean policy rather than an inherited cooldown.
        clearSuppression(userId)
        showSuccessToast()
        return
      }
      // registerPush CARRIES the underlying error out; report it before it is
      // dropped. This used to end here with only a status, and the consequence was
      // measured on production 2026-08-14: a user hit "Nie udało się włączyć push"
      // in Chrome, the whole cause existed in this object, and diagnosing it took
      // SSH access plus the edge access log to establish only that no request had
      // been made at all. The failures that land here — a blocked FCM endpoint, an
      // OS-level notification block, an unsupported browser — are all named
      // precisely by the thrown error and by nothing else we hold.
      // console.error deliberately, and nothing else: the app has no manual
      // error-reporting API (bayawatch.ts installs global handlers and exports no
      // capture function), and a caught error never reaches those. So the console is
      // the only channel that exists today. It is enough for the case this is for —
      // "open DevTools, press Enable, read the cause" — and inventing a reporting
      // helper here would be a second, unrelated change.
      if (result.status === 'error' && result.error) {
        console.error('[push] registration failed', result.error)
      }

      // Everything else stops asking for THIS SESSION only. None of these is a
      // policy decision by the user: a denial is already permanent via the
      // permission state, and unsupported/error are both potentially transient.
      // Suppressing for the session is what stops the gate from re-revealing
      // the card seconds later off its now-stale cached permission.
      suppressForSession()
      toast({ variant: 'destructive', title: t(`settings.notifications.push.${result.status}`) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      role="region"
      aria-label={t('settings.notifications.pushPrompt.title')}
      data-cy="push-prompt-card"
      /* Bottom-anchored but clear of the mobile FAB, which is fixed at
         `bottomTabBarHeight + safe-area + 16px` and is 56px tall (MobileFab.tsx)
         — hence the 84px offset here. On desktop it sits bottom-LEFT beside the
         sidebar, because the toast viewport owns bottom-right (Toast.tsx). z-95
         clears the FAB (z-90) while staying under toasts (z-99999).

         The mobile offset is a Tailwind arbitrary value, NOT an inline style: an
         inline `bottom` wins over `md:bottom-4` unconditionally, which left the
         desktop card floating ~140px up (the tab-bar var keeps its value at
         desktop widths). Underscores are Tailwind's escape for the spaces that
         `calc` requires around its operators. */
      className="fixed inset-x-4 bottom-[calc(var(--bottomTabBarHeight)_+_var(--safe-area-inset-bottom)_+_84px)] z-[95] rounded-lg border border-zinc-200 bg-white p-4 shadow-lg dark:border-zinc-800 dark:bg-zinc-950 md:inset-x-auto md:bottom-4 md:left-[calc(var(--sidebar-width)_+_1rem)] md:w-[380px]"
    >
      <div className="flex items-start gap-3">
        <Bell className="mt-0.5 size-5 shrink-0 text-zinc-500" aria-hidden="true" />
        <div className="flex flex-col gap-1">
          <p className="text-sm font-medium">{t('settings.notifications.pushPrompt.title')}</p>
          <p className="text-sm text-muted-foreground">
            {t('settings.notifications.pushPrompt.body')}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="-mr-2 -mt-2 ml-auto size-8 shrink-0"
          disabled={busy}
          aria-label={t('settings.notifications.pushPrompt.later')}
          onClick={() => snooze(userId)}
        >
          <X className="size-4" />
        </Button>
      </div>
      <div className="mt-4 flex items-center gap-2">
        <Button size="sm" disabled={busy} onClick={onEnable} data-cy="push-prompt-enable">
          {t('settings.notifications.pushPrompt.enable')}
        </Button>
        <Button variant="ghost" size="sm" disabled={busy} onClick={() => snooze(userId)}>
          {t('settings.notifications.pushPrompt.later')}
        </Button>
        {/* Explicit opt-out. This exists because the policy has no lifetime cap
            on snoozes (see pushPromptDecision.ts) — without a "never" control,
            the only way out would be an unmeasured cap. */}
        <Button
          variant="link"
          size="sm"
          className="ml-auto text-xs text-muted-foreground"
          disabled={busy}
          onClick={() => dismissForever(userId)}
        >
          {t('settings.notifications.pushPrompt.never')}
        </Button>
      </div>
    </div>
  )
}
